import axios from 'axios'
import toast from 'react-hot-toast'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import axiosInstance from '../../api/axiosInstance'
import OrderItemsTable from '../tables/OrderItemsTable'
import ConfirmationModal from '../modals/ConfirmationModal'
import FormModal from '../modals/FormModal'
import Ciudades from '../../../src/assets/data/ciudades.json'
import Departamentos from '../../../src/assets/data/departamentos.json'
import {metodoPago, transportadora, ORDER_STATES, tiposDeDocumento} from '../../assets/data/arrays'
import { Input, Textarea, Button } from '../ui'
import { FormField } from '../ui/Form'
import { XMarkIcon, CheckCircleIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { getFieldIcon } from '../../utils/formIcons'
import { copyToClipboard } from '../../utils/clipboard'
import { validateBoldPayment } from '../../services/payments/boldService'
import { runOfimaChainBeforeFirstCierre, postRecaudoCierre } from '../../services/orders/planSepareOfimaFlow'
import { NotesModal } from './shared'
import LoadingSpinner from '../LoadingSpinner'
import { mapApiProductToOrderDetails } from '../../utils/productStockDisplayUtils'
import { isCmdmPaymentMethod } from '../../utils/cmdmCreditDisplayUtils'
import VerifyCmdmCreditModal from './verify-transaction/VerifyCmdmCreditModal'

interface PagoParcial {
  id?: number
  metodo_pago: string
  valor: number
  id_transaccion?: string
  forma_pago?: string
  plazo?: number
  pago_verificado?: number
  fecha_creacion?: string
}

interface OrderDetail {
  id: number
  orders_id: number
  origin_order_id: number
  store_name: string
  status: string
  total: string
  envio?: string
  guia?: string
  date_created: string
  date_modified: string
  id_remision?: string
  agente?: number | {
    id: number
    first_name: string
    last_name: string
  }
  payment_method?: string
  extraDetails?: {
    forma_pago?: string
    payment_method?: string
    customer_note?: string
    transportadora?: string
    transaction_id?: string
    plazo?: string | number
    cliente_neto?: boolean | string | number
    recoge_en_bodega?: number
    pagos?: PagoParcial[]
  }
  customer: {
    first_name: string
    last_name: string
    email: string
    document?: string
    tipo_documento?: string
    tipo_persona?: string
  }
  billingAddress: {
    address_1: string
    address_2?: string
    city: string
    state: string
    country: string
    postcode: string
    phone?: string
    company?: string
  }
  shippingAddress: {
    address_1: string
    address_2?: string
    city: string
    state: string
    country: string
    postcode: string
    phone?: string
  }
  lineItems: Array<{
    product_id: string
    product_name: string
    quantity: number
    price: string
    is_combo?: number
    components?: Array<{
      id_combo?: number
      sku: string
      quantity: number
      name: string
      price: string
      tax_rate: string
      ean12?: string
      peso?: string | null
      longitud?: string | null
      ancho?: string | null
      alto?: string | null
      instrucciones_empaque?: string
      imagen?: string
    }>
  }>
}
interface VerifyTransactionsFormProps {
  orderDetail?: OrderDetail | null
  onRefreshOrderDetail?: () => Promise<void> | void
}

const normalizePaymentMethod = (value?: string | null) => (value ?? '').toString().trim().toLowerCase()

const isMultiPaymentMethod = (value?: string | null) => {
  const normalized = normalizePaymentMethod(value)
  return normalized === 'part' || normalized === 'plan_separe'
}

const requiresTransactionIdInVerify = (method?: string | null) => {
  const normalized = normalizePaymentMethod(method)
  return normalized === 'wompi' || normalized === 'mpago' || normalized === 'bacs' || normalized === 'bold' || normalized === 'dataf'
}

const requiresTransactionIdInPlanSepare = (method?: string | null) => {
  const normalized = normalizePaymentMethod(method)
  return requiresTransactionIdInVerify(normalized) || normalized === 'addi'
}

const getVerifyPaymentErrorMessage = (error: unknown, isPlanSepareFlow: boolean) => {
  const defaultMessage = isPlanSepareFlow
    ? 'No se pudo aplicar el abono. Intenta nuevamente.'
    : 'Error al verificar el pago'

  if (!axios.isAxiosError(error)) {
    return defaultMessage
  }

  const data = error.response?.data
  const apiMessage = typeof data?.message === 'string'
    ? data.message
    : (typeof data?.error === 'string' ? data.error : null)

  if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
    return isPlanSepareFlow
      ? 'Tiempo de espera agotado al aplicar el abono. Verifica la conexión e intenta de nuevo.'
      : 'Tiempo de espera agotado al verificar el pago. Verifica la conexión e intenta de nuevo.'
  }

  if (!error.response) {
    return isPlanSepareFlow
      ? 'No se pudo aplicar el abono por un problema de red o del servidor. Intenta nuevamente.'
      : 'No se pudo verificar el pago por un problema de red o del servidor. Intenta nuevamente.'
  }

  if (apiMessage) {
    return apiMessage
  }

  if (error.response.status >= 500) {
    return isPlanSepareFlow
      ? 'No se pudo aplicar el abono por un error interno del servidor.'
      : 'No se pudo verificar el pago por un error interno del servidor.'
  }

  return defaultMessage
}

const shouldRefreshOrderOnDatafTransactionRequired = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false
  const data = error.response?.data
  if (!data || typeof data !== 'object') return false

  const payload = data as Record<string, unknown>
  const errorText = typeof payload.error === 'string' ? payload.error.toLowerCase() : ''
  const messageText = typeof payload.message === 'string' ? payload.message.toLowerCase() : ''
  const combined = `${errorText} ${messageText}`

  return (
    combined.includes('transaction_id requerido') ||
    (combined.includes('id de transacción') && combined.includes('requerido') && combined.includes('dataf'))
  )
}

const pedidoEjecutarYConsultarSinExito = (rec: Record<string, unknown>): boolean => {
  const p = rec.Procesado
  if (p === false || p === 0 || p === '0') return true
  if (typeof p === 'string' && p.toLowerCase() === 'false') return true
  const nro = rec.NumeroOfima
  const sinRemision =
    nro === undefined || nro === null || String(nro).trim() === ''
  return sinRemision && typeof rec.Mensaje === 'string' && rec.Mensaje.trim() !== ''
}

/** Mensajes por ítem cuando el backend devuelve pedidos[].Mensaje (ejecutar-y-consultar). */
const collectEjecutarYConsultarPedidosDetalle = (data: Record<string, unknown>): string[] => {
  const pedidos = data.pedidos
  if (!Array.isArray(pedidos)) return []
  const out: string[] = []
  for (const item of pedidos) {
    if (!item || typeof item !== 'object') continue
    const rec = item as Record<string, unknown>
    if (!pedidoEjecutarYConsultarSinExito(rec)) continue
    const num = rec.Numero !== undefined && rec.Numero !== null ? String(rec.Numero) : ''
    const mensaje = typeof rec.Mensaje === 'string' ? rec.Mensaje.trim() : ''
    if (!mensaje) continue
    out.push(num ? `Pedido ${num}: ${mensaje}` : mensaje)
  }
  return out
}

/** Texto útil del error para el modal Plan Separe (API + ejecutar-y-consultar pedidos[].Mensaje). */
const getPlanSepareApiErrorBodyText = (err: unknown): string | null => {
  if (axios.isAxiosError(err)) {
    const raw = err.response?.data
    if (!raw || typeof raw !== 'object') return null
    const d = raw as Record<string, unknown>

    const msg = typeof d.message === 'string' ? d.message.trim() : ''
    const errStr = typeof d.error === 'string' ? d.error.trim() : ''

    const nested = d.data && typeof d.data === 'object' ? (d.data as Record<string, unknown>) : null
    const msgNested = nested && typeof nested.message === 'string' ? nested.message.trim() : ''
    const errNested = nested && typeof nested.error === 'string' ? nested.error.trim() : ''

    const chosen = msg || msgNested || errStr || errNested
    const pedidosDetalle = collectEjecutarYConsultarPedidosDetalle(d)
    const pedidosStr = pedidosDetalle.length ? pedidosDetalle.join(' · ') : ''

    if (chosen && /Request failed with status code/i.test(chosen)) {
      return pedidosStr || null
    }

    const parts = [chosen, pedidosStr].filter(s => s && !/Request failed with status code/i.test(s))
    const merged = parts.join(parts.length > 1 ? ' — ' : '')
    return merged.trim() ? merged.trim() : null
  }

  if (err instanceof Error) {
    const m = err.message?.trim() ?? ''
    if (!m || /Request failed with status code/i.test(m)) return null
    return m
  }

  return null
}

/** Misma base que en OrderPaymentInfo: comprobante / checkout Bold por payment_id */
const BOLD_CHECKOUT_PAYMENT_BASE_URL = 'https://checkout.bold.co/payment/'

const BoldCheckoutLinkBelowId: React.FC<{ paymentId: string }> = ({ paymentId }) => {
  const trimmed = paymentId.trim()
  if (!trimmed) return null
  const url = `${BOLD_CHECKOUT_PAYMENT_BASE_URL}${trimmed}`
  return (
    <div className="mt-1 flex items-center gap-1">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 flex-1 truncate text-[11px] text-gray-500 hover:text-blue-600 hover:underline"
        aria-label="Abrir comprobante de pago Bold en nueva pestaña"
      >
        {url}
      </a>
      <button
        type="button"
        onClick={async () => {
          const ok = await copyToClipboard(url)
          if (ok) toast.success('Link copiado', { icon: '📋', position: 'top-center' })
        }}
        className="flex-shrink-0 rounded p-0.5 text-gray-500 transition-colors hover:bg-blue-50/50 hover:text-blue-500"
        title="Copiar link"
        aria-label="Copiar link de comprobante Bold"
      >
        <DocumentDuplicateIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

type PlanSepareModalUiPhase = 'idle' | 'progress' | 'error'

interface PlanSepareModalUiState {
  phase: PlanSepareModalUiPhase
  step: number
  total: number
  label: string
  errorMessage: string | null
}

const initialPlanSepareModalUi: PlanSepareModalUiState = {
  phase: 'idle',
  step: 0,
  total: 0,
  label: '',
  errorMessage: null
}

const VerifyTransactionsForm: React.FC<VerifyTransactionsFormProps> = ({ orderDetail, onRefreshOrderDetail }) => {
  const orderPaymentMethodRaw = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
  const isPlanSepareMultiOrder =
    normalizePaymentMethod(orderPaymentMethodRaw) === 'plan_separe' && isMultiPaymentMethod(orderPaymentMethodRaw)

  const [productDetails, setProductDetails] = useState<{[key: string]: any}>({})
  const [loadingDetails, setLoadingDetails] = useState<{[key: string]: boolean}>({})
  const [showVerifyPaymentModal, setShowVerifyPaymentModal] = useState(false)
  const [showCmdmCreditModal, setShowCmdmCreditModal] = useState(false)
  const [showCashModal, setShowCashModal] = useState(false)
  const [transactionId, setTransactionId] = useState('')
  const [montoRecibido, setMontoRecibido] = useState('')
  const [verifyingPayment, setVerifyingPayment] = useState(false)
  const [planSepareModalUi, setPlanSepareModalUi] = useState<PlanSepareModalUiState>(initialPlanSepareModalUi)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [customerNote, setCustomerNote] = useState('')
  // Estados para pagos parciales: objeto con índice del pago como clave
  const [pagosParcialesData, setPagosParcialesData] = useState<{[key: number]: {monto?: string; transactionId?: string}}>({})
  const [expandedCombos, setExpandedCombos] = useState<{[key: string]: boolean}>(() => {
    const initialExpanded: {[key: string]: boolean} = {}
    if (orderDetail?.lineItems) {orderDetail.lineItems.forEach(item => { initialExpanded[item.product_id] = true;})}
    return initialExpanded
  })

  const isClienteNetoActive = (() => {
    const raw = orderDetail?.extraDetails?.cliente_neto as unknown
    if (typeof raw === 'boolean') return raw
    if (typeof raw === 'number') return raw === 1
    if (typeof raw === 'string') {
      const v = raw.trim().toLowerCase()
      return v === '1' || v === 'true'
    }
    return false
  })()

  // Ref del último pedido con el que se inicializó el modal: evita sobrescribir el input al re-render
  // pero permite re-inicializar si el usuario cambia de pedido con el modal abierto
  const lastInitializedOrderIdRef = useRef<number | null>(null)

  const resetPlanSepareModalUi = useCallback(() => {
    setPlanSepareModalUi(initialPlanSepareModalUi)
  }, [])

  useEffect(() => {
    if (!showVerifyPaymentModal) {
      resetPlanSepareModalUi()
    }
  }, [showVerifyPaymentModal, resetPlanSepareModalUi])

  useEffect(() => {
    if (orderDetail) {
      loadProductDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail])

  // Sincronizar notas del pedido cuando cambia el pedido seleccionado
  useEffect(() => {
    setCustomerNote(orderDetail?.extraDetails?.customer_note?.trim() ?? '')
  }, [orderDetail?.id, orderDetail?.extraDetails?.customer_note])

  // Inicializar transaction_id / pagos parciales: al abrir el modal o al cambiar de pedido (mismo modal abierto)
  useEffect(() => {
    if (showVerifyPaymentModal) {
      const paymentMethod = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
      const isPart = isMultiPaymentMethod(paymentMethod)
      const orderId = orderDetail?.id ?? null

      // Re-inicializar solo si es otro pedido o primera vez; si es el mismo pedido, no pisar lo que escribió el usuario
      const mustInitialize = orderId !== lastInitializedOrderIdRef.current

      if (mustInitialize) {
        lastInitializedOrderIdRef.current = orderId
        if (isPart) {
          const pagos = orderDetail?.extraDetails?.pagos || []
          const datosIniciales: {[key: number]: {monto?: string; transactionId?: string}} = {}
          pagos.forEach((pago, index) => {
            if (pago.id_transaccion) {
              datosIniciales[index] = {
                ...datosIniciales[index],
                transactionId: pago.id_transaccion
              }
            }
          })
          setPagosParcialesData(datosIniciales)
        } else {
          const existingTransactionId = orderDetail?.extraDetails?.transaction_id || ''
          setTransactionId(existingTransactionId)
        }
      }
    } else {
      lastInitializedOrderIdRef.current = null
      setPagosParcialesData({})
      setTransactionId('')
      setMontoRecibido('')
    }
  }, [showVerifyPaymentModal, orderDetail])

  const getCiudadNombre = (codigo: string) => {
    const ciudad = Ciudades.find(c => c.codigo === codigo)
    return ciudad ? ciudad.ciudad : codigo
  }

  const getDepartamentoNombre = (codigo: string) => {
    const depto = Departamentos.find(d => d.Codigo.toString() === codigo)
    return depto ? depto.Nombre : codigo
  }

  const getOrderStateName = (status: string) => { const state = ORDER_STATES.find(s => s.value === status); return state ? state.label : status;}

  const getTipoDocumentoName = (codigo: string) => {
    const tipoDoc = tiposDeDocumento.find(t => t.value === codigo)
    return tipoDoc ? tipoDoc.label : codigo
  }

  const getPaymentMethodName = (method: string) => {
    const paymentMethod = metodoPago.find(p => p.value === method)
    return paymentMethod ? paymentMethod.label : method
  }

  const formatPagoValorCop = (valor: unknown) => {
    const num = Number(valor)
    if (Number.isNaN(num) || num <= 0) return '—'
    return `$${Math.round(num).toLocaleString('es-CO')}`
  }

  const getTransportadoraName = (transportadoraValue: string) => {
    const transportadoraItem = transportadora.find(t => t.value === transportadoraValue)
    return transportadoraItem ? transportadoraItem.label : transportadoraValue
  }

  // Función para cargar los detalles de los productos
  const loadProductDetails = async () => {
    if (orderDetail?.lineItems?.length) {
      try {
        console.log('🚀 Iniciando carga de detalles de productos...')
        const newDetails: {[key: string]: any} = {}
        const newLoadingDetails: {[key: string]: boolean} = {}
        const allSkus: string[] = []
        orderDetail.lineItems.forEach(item => {allSkus.push(item.product_id);})
        
        // Agregar SKUs de componentes de combos
        orderDetail.lineItems.forEach(item => {
          if (item.is_combo === 1 && item.components) {
              item.components.forEach((component: any) => {allSkus.push(component.sku);})
          }})
        
        const uniqueSkus: string[] = Array.from(new Set(allSkus))
    
        uniqueSkus.forEach(sku => { newLoadingDetails[sku] = true; setLoadingDetails(prev => ({...prev, [sku]: true}));})
        
        try { console.log('Cargando detalles de todos los productos:', uniqueSkus)
          const token = localStorage.getItem('accessToken')
          const response = await axiosInstance.get(`/products/multiple?skus=${uniqueSkus.join(',')}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          
          if (response.data && response.data.products) {
            response.data.products.forEach((product: any) => {
              const sku = product.sku?.toString().replace(/^0+/, '') || ''
              newDetails[sku] = mapApiProductToOrderDetails({ ...product, sku })
            })
          }
          
          // Procesar combos para calcular stock mínimo
          orderDetail.lineItems.forEach(item => {
            if (item.is_combo === 1 && item.components) {
              const stocks: number[] = []
              item.components.forEach((component: any) => {
                const componentDetails = newDetails[component.sku]
                if (componentDetails) {
                  const stockValue = componentDetails.stock || componentDetails.stock_quantity || 0
                  stocks.push(stockValue)
                } else {stocks.push(0);}
              })
              
              // Actualizar stock del combo con el mínimo
              if (stocks.length > 0) {
                const minStock = Math.min(...stocks)
                if (newDetails[item.product_id]) {
                    newDetails[item.product_id] = {
                    ...newDetails[item.product_id],
                    stock: minStock,
                    stock_quantity: minStock
                }
              }
            }
            }
          })
          
        } catch (error) {
          console.error('Error al cargar productos múltiples:', error)
        } finally {
          // Marcar todos como no cargando
          uniqueSkus.forEach(sku => {
            newLoadingDetails[sku] = false
            setLoadingDetails(prev => ({...prev, [sku]: false}))
          })
        }

        console.log('✅ Todos los detalles de productos cargados:', newDetails)
        setProductDetails(newDetails)
      } catch (error) {
        console.error('Error general al cargar detalles:', error)
      }
    }
  }


  const toggleCombo = async (productId: string) => {
    setExpandedCombos(prev => ({...prev,[productId]: !prev[productId]}))

    // Si se está expandiendo el combo, cargar los detalles de los componentes
    if (!expandedCombos[productId]) {
      const productDetail = productDetails[productId]
      if (productDetail?.is_combo === 1 && productDetail?.components) {
        let stocks = []
        
        // Cargar detalles de cada componente
        for (const component of productDetail.components) {
          const compSku = component.sku
          if (!productDetails[compSku] && compSku) {
            setLoadingDetails(prev => ({ ...prev, [compSku]: true }))
            try {
              console.log('Cargando detalles del componente por SKU:', compSku)
              const token = localStorage.getItem('accessToken')
              const response = await axiosInstance.get(`/products/${compSku}`, {
                headers: { Authorization: `Bearer ${token}` }
              })
              if (response.data) {
                const mappedComp = mapApiProductToOrderDetails(response.data)
                stocks.push(mappedComp.stock)
                setProductDetails(prev => ({
                  ...prev,
                  [compSku]: mappedComp
                }))
              }
            } catch (error) {
              console.error('Error al cargar el componente:', compSku, error)
              stocks.push(0)
            } finally {
              setLoadingDetails(prev => ({ ...prev, [compSku]: false }))
            }
          } else if (productDetails[compSku]) {
            const stockValue = productDetails[compSku].stock || productDetails[compSku].stock_quantity || 0
            stocks.push(stockValue)
          }
        }

        // Una vez que tenemos todos los stocks, actualizamos el producto principal
        if (stocks.length > 0) {
          const minStock = Math.min(...stocks)
          console.log('Stocks de componentes:', stocks)
          console.log('Stock mínimo para el combo:', minStock)
          
          setProductDetails(prev => ({
            ...prev,
            [productId]: {
              ...prev[productId],
              stock: minStock,
              stock_quantity: minStock
            }
          }))
        }
      }
    }
  }

  const calculateTotals = (items: any[] = []) => {
    const subtotal = items.reduce((sum, item) => {
      return sum + (parseFloat(item.price) * item.quantity)
    }, 0)

    const discount = items.reduce((sum, item) => {
      const d = item.discount !== undefined && item.discount !== null ? Number(item.discount) : 0
      return sum + (isNaN(d) ? 0 : d)
    }, 0)

    const taxes = isClienteNetoActive ? 0 : items.reduce((sum, item) => {
      const itemSubtotal = parseFloat(item.price) * item.quantity
      const itemDiscount = item.discount !== undefined && item.discount !== null ? Number(item.discount) : 0
      
      // Buscar el IVA en productDetails usando el SKU del item
      const productDetail = productDetails[item.sku || item.product_id] || {}
      const itemIva = productDetail.iva_ofima !== undefined && productDetail.iva_ofima !== null 
        ? Number(productDetail.iva_ofima) / 100 
        : (item.iva !== undefined && item.iva !== null ? Number(item.iva) / 100 : 0.19)
        
      return sum + ((itemSubtotal - itemDiscount) * itemIva)
    }, 0)

    const shipping = parseFloat(orderDetail?.envio || '0')
    const total = subtotal - discount + taxes

    return {
      subtotal: subtotal.toFixed(2),
      discount: discount.toFixed(2),
      taxes: taxes.toFixed(2),
      shipping: shipping.toFixed(2),
      total: total.toFixed(2)
    }
  }

  const cmdmPedidoTotalCop = useMemo(() => {
    if (!orderDetail?.lineItems?.length) return null
    const totals = calculateTotals(orderDetail.lineItems)
    const totalPedido = parseFloat(totals.total) + parseFloat(orderDetail?.envio || '0')
    if (Number.isNaN(totalPedido) || totalPedido <= 0) return null
    return Math.round(totalPedido)
  }, [orderDetail?.lineItems, orderDetail?.envio, productDetails, isClienteNetoActive])

  const buildCmdmCreditPatch = (status: string, pagoVerificado: boolean) => {
    const pmRaw = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
    const extra: Record<string, unknown> = {
      ...orderDetail?.extraDetails,
      customer_note: customerNote.trim() || '',
      payment_method: pmRaw
    }
    return {
      status,
      payment_method: pmRaw,
      pago_verificado: pagoVerificado ? 1 : 0,
      old_data_order: {
        status: orderDetail?.status || 'verify_transaction',
        customer_note: customerNote.trim() || '',
        line_items: orderDetail?.lineItems || [],
        transaction_id: ''
      },
      extraDetails: extra
    }
  }

  const finalizeCmdmCreditVerification = async () => {
    try {
      await Promise.resolve(onRefreshOrderDetail?.())
    } catch (refreshErr) {
      console.error('Error al actualizar el detalle del pedido tras verificar credito MDM:', refreshErr)
    }
    setShowCmdmCreditModal(false)
    setShowSuccessModal(true)
  }

  const handleCmdmApproveCredit = async () => {
    if (!orderDetail?.id) {
      toast.error('No se encontro el pedido', { icon: '⚠️', position: 'top-center' })
      return
    }
    const token = localStorage.getItem('accessToken')
    if (!token) {
      toast.error('Error de autenticacion', { icon: '⚠️', position: 'top-center' })
      return
    }
    try {
      setVerifyingPayment(true)
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
      await axiosInstance.patch(
        `/list/${orderDetail.id}`,
        buildCmdmCreditPatch('processing', true),
        { headers }
      )
      const transportadoraValue = orderDetail?.extraDetails?.transportadora || ''
      if (transportadoraValue === 'Entrega PDV') {
        toast.success('Credito aprobado, en proceso de asignacion...', { icon: '✅', position: 'top-center' })
        await new Promise(resolve => setTimeout(resolve, 2000))
        await axiosInstance.patch(
          `/list/${orderDetail.id}`,
          buildCmdmCreditPatch('asignado', true),
          { headers }
        )
        toast.success('Credito aprobado y pedido asignado exitosamente', { icon: '✅', position: 'top-center' })
      } else {
        toast.success('Credito MDM aprobado correctamente', { icon: '✅', position: 'top-center' })
      }
      await finalizeCmdmCreditVerification()
    } catch (error) {
      console.error('Error al aprobar credito MDM:', error)
      toast.error(getVerifyPaymentErrorMessage(error, false), { icon: '⚠️', position: 'top-center' })
    } finally {
      setVerifyingPayment(false)
    }
  }

  const handleCmdmRejectCredit = async () => {
    if (!orderDetail?.id) {
      toast.error('No se encontro el pedido', { icon: '⚠️', position: 'top-center' })
      return
    }
    const token = localStorage.getItem('accessToken')
    if (!token) {
      toast.error('Error de autenticacion', { icon: '⚠️', position: 'top-center' })
      return
    }
    try {
      setVerifyingPayment(true)
      await axiosInstance.patch(
        `/list/${orderDetail.id}`,
        buildCmdmCreditPatch('cancelled', false),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )
      toast.success('Credito MDM rechazado', { icon: '✅', position: 'top-center' })
      setShowCmdmCreditModal(false)
      try {
        await Promise.resolve(onRefreshOrderDetail?.())
      } catch (refreshErr) {
        console.error('Error al actualizar el detalle tras rechazar credito MDM:', refreshErr)
      }
      window.location.reload()
    } catch (error) {
      console.error('Error al rechazar credito MDM:', error)
      toast.error(getVerifyPaymentErrorMessage(error, false), { icon: '⚠️', position: 'top-center' })
    } finally {
      setVerifyingPayment(false)
    }
  }
  // Función para verificar el pago
  const handleVerifyPayment = async () => {
    const paymentMethodRaw = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
    const paymentMethodNorm = normalizePaymentMethod(paymentMethodRaw)
    const isCOD = paymentMethodNorm === 'cod'
    const isCash = paymentMethodNorm === 'cash'
    const isPart = isMultiPaymentMethod(paymentMethodRaw)
    const isPlanSepare = paymentMethodNorm === 'plan_separe'
    
    // Validaciones según el tipo de pago
    if (isPart) {
      // Validar pagos parciales
      const pagos = orderDetail?.extraDetails?.pagos || []
      if (pagos.length === 0) {
        toast.error(
          isPlanSepare ? 'No hay abonos para verificar' : 'No hay pagos parciales para verificar',
          { icon: '⚠️', position: 'top-center' }
        )
        return
      }
      
      // Validar que todos los pagos parciales tengan sus datos completos
      for (let i = 0; i < pagos.length; i++) {
        const pago = pagos[i]
        if (Number(pago.pago_verificado) === 1) {
          continue
        }
        const pagoData = pagosParcialesData[i] || {}
        const filaVerif = isPlanSepare ? `abono ${i + 1}` : `pago ${i + 1}`

        if (pago.metodo_pago === 'cod' || pago.metodo_pago === 'cash') {
          // Limpiar puntos y comas del monto
          const montoLimpio = (pagoData.monto || '').replace(/[.,]/g, '')
          if (!montoLimpio.trim() || isNaN(Number(montoLimpio)) || Number(montoLimpio) <= 0) {
            toast.error(`Por favor ingrese un monto válido para el ${filaVerif}`, { icon: '⚠️', position: 'top-center' })
            return
          }
          // Validar que el monto coincida con el valor del pago
          if (pago.metodo_pago === 'cod' || pago.metodo_pago === 'cash') {
            const valorPago = Math.round(Number(pago.valor) || 0)
            const montoIngresado = Math.round(Number(montoLimpio))
            if (montoIngresado !== valorPago) {
              toast.error(
                `El monto ingresado ($${montoIngresado.toLocaleString('es-CO')}) no coincide con el valor del ${filaVerif} ($${valorPago.toLocaleString('es-CO')})`,
                { icon: '⚠️', position: 'top-center' }
              )
              return
            }
          }
        } else if ((isPlanSepare && requiresTransactionIdInPlanSepare(pago.metodo_pago)) || (!isPlanSepare && requiresTransactionIdInVerify(pago.metodo_pago))) {
          if (!pagoData.transactionId || !pagoData.transactionId.trim()) {
            toast.error(`Por favor ingrese el ID de transacción para el ${filaVerif}`, { icon: '⚠️', position: 'top-center' })
            return
          }
        }
      }
    } else if (isCOD || isCash) {
      // Limpiar puntos y comas del monto
      const montoLimpio = montoRecibido.replace(/[.,]/g, '')
      if (!montoLimpio.trim() || isNaN(Number(montoLimpio)) || Number(montoLimpio) <= 0) {
        toast.error("Por favor ingrese un monto válido", { icon: "⚠️" })
        return
      }
      
      // Validar que el monto en efectivo coincida con el total del pedido
      if (isCash) {
        const totals = calculateTotals(orderDetail?.lineItems || [])
        const totalPedido = parseFloat(totals.total) + parseFloat(orderDetail?.envio || '0')
        const montoIngresado = Math.round(Number(montoLimpio))
        const totalRedondeado = Math.round(totalPedido)
        
        if (montoIngresado !== totalRedondeado) {
          toast.error(
            `El monto ingresado ($${montoIngresado.toLocaleString('es-CO')}) no coincide con el total del pedido ($${totalRedondeado.toLocaleString('es-CO')})`, 
            { icon: "⚠️", position: 'top-center' }
          )
          return
        }
      }
    } else {
      if (!transactionId.trim()) {
        toast.error("Por favor ingrese el ID de transacción", { icon: "⚠️", position: 'top-center' })
        return
      }
    }

    try {
      setVerifyingPayment(true)
      const token = localStorage.getItem("accessToken")
      if (!token) {
        toast.error("Error de autenticación", { icon: "⚠️", position: 'top-center' })
        return
      }

      // Validación de pagos Bold antes del PATCH
      const isBold = paymentMethodNorm === 'bold'
      
      if (isBold) {
        // Validar pago Bold directo
        if (!transactionId.trim()) {
          toast.error("Por favor ingrese el ID de transacción de Bold", { icon: "⚠️", position: 'top-center' })
          setVerifyingPayment(false)
          return
        }

        const boldValidation = await validateBoldPayment(transactionId.trim(), orderDetail?.store_name)
        
        if (!boldValidation.success) {
          toast.error(boldValidation.error || "Error al validar el pago de Bold", { icon: "⚠️", position: 'top-center' })
          setVerifyingPayment(false)
          return
        }

        if (!boldValidation.valid) {
          // Si el pago está rechazado, cambiar estado a "pending"
          const updateDataPending = {
            status: "pending",
            old_data_order: {
              status: orderDetail?.status || "verify_transaction",
              customer_note: customerNote.trim() || "",
              line_items: orderDetail?.lineItems || [],
              transaction_id: transactionId.trim()
            },
            extraDetails: {
              ...orderDetail?.extraDetails,
              customer_note: customerNote.trim() || "",
              transaction_id: transactionId.trim()
            }
          }

          try {
            await axiosInstance.patch(`/list/${orderDetail?.id}`, updateDataPending, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            })
          } catch (error) {
            console.error("Error al cambiar estado a pending:", error)
          }

          toast.error(boldValidation.error || "El pedido no puede ser verificado. Transacción rechazada por el banco.", { icon: "⚠️", position: 'top-center' })
          setVerifyingPayment(false)
          setShowVerifyPaymentModal(false)
          return
        }
      } else if (isPart) {
        // Validar pagos parciales que incluyan Bold
        const pagos = orderDetail?.extraDetails?.pagos || []
        
        for (let i = 0; i < pagos.length; i++) {
          const pago = pagos[i]
          const pagoData = pagosParcialesData[i] || {}
          
          if (pago.metodo_pago === 'bold') {
            const paymentId = pagoData.transactionId?.trim() || pago.id_transaccion?.trim()
            
            if (!paymentId) {
              const filaBold = isPlanSepare ? `abono Bold ${i + 1}` : `pago Bold ${i + 1}`
              toast.error(`Por favor ingrese el ID de transacción para el ${filaBold}`, { icon: '⚠️', position: 'top-center' })
              setVerifyingPayment(false)
              return
            }

            const boldValidation = await validateBoldPayment(paymentId, orderDetail?.store_name)
            
            if (!boldValidation.success) {
              const filaBoldErr = isPlanSepare ? `abono Bold ${i + 1}` : `pago Bold ${i + 1}`
              toast.error(`Error al validar el ${filaBoldErr}: ${boldValidation.error}`, { icon: '⚠️', position: 'top-center' })
              setVerifyingPayment(false)
              return
            }

            if (!boldValidation.valid) {
              // Si el pago está rechazado, cambiar estado a "pending"
              const updateDataPending = {
                status: "pending",
                old_data_order: {
                  status: orderDetail?.status || "verify_transaction",
                  customer_note: customerNote.trim() || "",
                  line_items: orderDetail?.lineItems || [],
                  transaction_id: orderDetail?.extraDetails?.transaction_id?.trim() || ""
                },
                extraDetails: {
                  ...orderDetail?.extraDetails,
                  customer_note: customerNote.trim() || ""
                }
              }

              try {
                await axiosInstance.patch(`/list/${orderDetail?.id}`, updateDataPending, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                })
              } catch (error) {
                console.error("Error al cambiar estado a pending:", error)
              }

              toast.error(`Pago Bold ${i + 1}: ${boldValidation.error || "El pedido no puede ser verificado. Transacción rechazada por el banco."}`, { icon: "⚠️", position: 'top-center' })
              setVerifyingPayment(false)
              setShowVerifyPaymentModal(false)
              return
            }
          }
        }
      }

      // Obtener el nombre del usuario del localStorage
      const storedUser = localStorage.getItem('user')
      const userName = storedUser ? JSON.parse(storedUser).first_name + ' ' + JSON.parse(storedUser).last_name : 'Usuario'

      const buildPagosActualizadosPart = (): any[] => {
        const paymentMethodLocal = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
        const isPlanSepareOrder = normalizePaymentMethod(paymentMethodLocal) === 'plan_separe'
        const pagos = orderDetail?.extraDetails?.pagos || []
        return pagos.map((pago, index) => {
          if (Number(pago.pago_verificado) === 1) {
            return {
              id: pago.id,
              metodo_pago: pago.metodo_pago,
              valor: typeof pago.valor === 'number' ? pago.valor.toString() : String(pago.valor || '0'),
              forma_pago: orderDetail?.extraDetails?.forma_pago || pago.forma_pago || '6',
              plazo: pago.plazo || 30,
              fecha_creacion: pago.fecha_creacion || new Date().toISOString(),
              id_transaccion: pago.id_transaccion ?? null,
              pago_verificado: 1
            }
          }
          const pagoData = pagosParcialesData[index] || {}
          const pagoActualizado: any = {
            id: pago.id,
            metodo_pago: pago.metodo_pago,
            valor: typeof pago.valor === 'number' ? pago.valor.toString() : String(pago.valor || '0'),
            forma_pago: orderDetail?.extraDetails?.forma_pago || pago.forma_pago || '6',
            plazo: pago.plazo || 30,
            fecha_creacion: pago.fecha_creacion || new Date().toISOString()
          }
          if (pago.metodo_pago === 'cod') {
            pagoActualizado.id_transaccion = null
            // Plan Separe: todo abono que se verifica aquí debe quedar pago_verificado = 1
            pagoActualizado.pago_verificado = isPlanSepareOrder ? 1 : 0
          } else if (pago.metodo_pago === 'cash') {
            pagoActualizado.id_transaccion = null
            pagoActualizado.pago_verificado = 1
          } else if (requiresTransactionIdInVerify(pago.metodo_pago) || (normalizePaymentMethod(paymentMethodLocal) === 'plan_separe' && normalizePaymentMethod(pago.metodo_pago) === 'addi')) {
            const txId = pagoData.transactionId?.trim() || pago.id_transaccion
            pagoActualizado.id_transaccion = txId || null
            pagoActualizado.pago_verificado = 1
          } else {
            pagoActualizado.id_transaccion = pago.id_transaccion || null
            pagoActualizado.pago_verificado = 1
          }
          return pagoActualizado
        })
      }

      // Función auxiliar para preparar los datos de actualización
      const prepararDatosActualizacion = (status: string) => {
        const pmRaw = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
        const pmNorm = normalizePaymentMethod(pmRaw)
        const isCOD = pmNorm === 'cod'
        const isCash = pmNorm === 'cash'
        const isPart = isMultiPaymentMethod(pmRaw)
        
        let updateData: any = {
          status: status,
          payment_method: pmRaw,
          old_data_order: {
            status: orderDetail?.status || "verify_transaction",
            customer_note: customerNote.trim() || "",
            line_items: orderDetail?.lineItems || [],
            transaction_id: orderDetail?.extraDetails?.transaction_id?.trim() || ""
          }
        }

        if (isPart) {
          const pagosActualizados = buildPagosActualizadosPart()
          
          // Verificar si todos los pagos están verificados
          const todosVerificados = pagosActualizados.every(p => p.pago_verificado === 1)
          
          // Los pagos van en el nivel raíz, no dentro de extraDetails
          const isPlanSeparePayload = pmNorm === 'plan_separe'
          updateData.pagos = isPlanSeparePayload
            ? pagosActualizados.map(({ pago_verificado, ...rest }: any) => rest)
            : pagosActualizados
          
          // Mantener extraDetails sin pagos (los pagos van en el nivel raíz), incluyendo notas editadas y método de pago
          updateData.extraDetails = {
            ...orderDetail?.extraDetails,
            customer_note: customerNote.trim() || "",
            payment_method: pmRaw
          }
          // Eliminar pagos de extraDetails si existe
          if (updateData.extraDetails.pagos) {
            delete updateData.extraDetails.pagos
          }
          
          // Solo agregar transacciones de efectivo si todos los pagos están verificados
          if (todosVerificados) {
            // Si todos están verificados, agregar transacciones de efectivo si hay COD
            const transaccionesEfectivo: Array<{fecha: string; valor: number; responsable: string}> = []
            
            pagosActualizados.forEach((pago, index) => {
              if ((pago.metodo_pago === 'cod' || pago.metodo_pago === 'cash') && pagosParcialesData[index]?.monto) {
                transaccionesEfectivo.push({
                  fecha: new Date().toISOString().slice(0, 19).replace('T', ' '),
                  valor: Number((pagosParcialesData[index].monto || '').replace(/[.,]/g, '')),
                  responsable: userName
                })
              }
            })
            
            if (transaccionesEfectivo.length > 0) {
              updateData.transacciones_efectivo = transaccionesEfectivo
            }
          }
        } else if (isCOD) {
          // Para pagos COD (contra entrega)
          updateData.pago_verificado = 0
          updateData.extraDetails = {
            ...orderDetail?.extraDetails,
            customer_note: customerNote.trim() || "",
            payment_method: pmRaw
          }
          updateData.transacciones_efectivo = [
            {
              fecha: new Date().toISOString().slice(0, 19).replace('T', ' '),
              valor: Number(montoRecibido.replace(/[.,]/g, '')),
              responsable: userName
            }
          ]
        } else if (isCash) {
          // Para pagos cash (efectivo)
          updateData.pago_verificado = true
          updateData.extraDetails = {
            ...orderDetail?.extraDetails,
            customer_note: customerNote.trim() || "",
            payment_method: pmRaw
          }
          updateData.transacciones_efectivo = [
            {
              fecha: new Date().toISOString().slice(0, 19).replace('T', ' '),
              valor: Number(montoRecibido.replace(/[.,]/g, '')),
              responsable: userName
            }
          ]
        } else {
          // Para pagos wompi, mpago, bacs, bold
          updateData.pago_verificado = true
          const newTransactionId = transactionId.trim()
          const baseExtraDetails: Record<string, unknown> = {
            ...orderDetail?.extraDetails,
            customer_note: customerNote.trim() || "",
            transaction_id: newTransactionId,
            payment_method: pmRaw
          }
          // Si existe un solo pago del mismo método, actualizar id_transaccion y pago_verificado para consistencia
          const existingPagos = orderDetail?.extraDetails?.pagos
          if (Array.isArray(existingPagos) && existingPagos.length === 1 && normalizePaymentMethod(existingPagos[0].metodo_pago) === pmNorm) {
            baseExtraDetails.pagos = [{
              ...existingPagos[0],
              id_transaccion: newTransactionId,
              pago_verificado: 1
            }]
          }
          updateData.extraDetails = baseExtraDetails
        }

        return updateData
      }

      const pmVerify = normalizePaymentMethod(orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method)
      const isPlanSepareVerify = pmVerify === 'plan_separe' && isMultiPaymentMethod(orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method)

      if (isPlanSepareVerify) {
        const od = orderDetail
        if (!od?.id) {
          toast.error('No se encontró el pedido', { icon: '⚠️', position: 'top-center' })
          return
        }
        const lista = buildPagosActualizadosPart()
        const prevPagos = od.extraDetails?.pagos || []
        const nuevosIdx: number[] = []
        lista.forEach((p, i) => {
          const prevOk = Number(prevPagos[i]?.pago_verificado) === 1
          const nowOk = Number(p.pago_verificado) === 1
          if (!prevOk && nowOk) nuevosIdx.push(i)
        })
        if (nuevosIdx.length === 0) {
          toast.error('No hay abonos nuevos POR VERIFICAR', { icon: '⚠️', position: 'top-center' })
          return
        }
        const idRemisionStr = od.id_remision
        try {
          const nuevosSet = new Set(nuevosIdx)
          const buildPlanSeparePagosSoloIdTransaccion = (): any[] => {
            const paymentMethodLocal = od.extraDetails?.payment_method || od.payment_method
            const isPlanSepareOrder = normalizePaymentMethod(paymentMethodLocal) === 'plan_separe'
            const pagos = od.extraDetails?.pagos || []
            return pagos.map((pago, index) => {
              const row: any = {
                id: pago.id,
                metodo_pago: pago.metodo_pago,
                valor: typeof pago.valor === 'number' ? pago.valor.toString() : String(pago.valor || '0'),
                forma_pago: od.extraDetails?.forma_pago || pago.forma_pago || '6',
                plazo: pago.plazo || 30,
                fecha_creacion: pago.fecha_creacion || new Date().toISOString()
              }
              const pagoData = pagosParcialesData[index] || {}
              if (!nuevosSet.has(index)) {
                row.id_transaccion = pago.id_transaccion ?? null
                return row
              }
              if (pago.metodo_pago === 'cod') {
                row.id_transaccion = null
              } else if (pago.metodo_pago === 'cash') {
                row.id_transaccion = null
              } else if (
                requiresTransactionIdInVerify(pago.metodo_pago) ||
                (isPlanSepareOrder && normalizePaymentMethod(pago.metodo_pago) === 'addi')
              ) {
                const txId = pagoData.transactionId?.trim() || pago.id_transaccion
                row.id_transaccion = txId || null
              } else {
                row.id_transaccion = pago.id_transaccion || null
              }
              return row
            })
          }

          const needsOfimaChain = !String(idRemisionStr || '').trim()
          const totalSteps = 1 + (needsOfimaChain ? 1 : 0) + 1 + nuevosIdx.length

          let done = 0
          const bumpProgress = (label: string) => {
            done += 1
            setPlanSepareModalUi({
              phase: 'progress',
              step: done,
              total: totalSteps,
              label,
              errorMessage: null
            })
          }

          setPlanSepareModalUi({
            phase: 'progress',
            step: 0,
            total: totalSteps,
            label: 'Preparando verificación de abonos…',
            errorMessage: null
          })

          bumpProgress('Guardando referencia de transacción (id_transacción)…')
          const paymentMethodPs = od.extraDetails?.payment_method || od.payment_method
          const pagosSoloTx = buildPlanSeparePagosSoloIdTransaccion()
          const extraTx: Record<string, unknown> = {
            ...od.extraDetails,
            customer_note: customerNote.trim() || '',
            payment_method: paymentMethodPs
          }
          if (extraTx.pagos) delete extraTx.pagos
          await axiosInstance.patch(`/list/${od.id}`, {
            status: od.status,
            payment_method: paymentMethodPs,
            old_data_order: {
              status: od.status || 'verify_transaction',
              customer_note: customerNote.trim() || '',
              line_items: od.lineItems || [],
              transaction_id: od.extraDetails?.transaction_id?.trim() || ''
            },
            pagos: pagosSoloTx,
            extraDetails: extraTx
          }, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          let nrodcto = ''
          if (needsOfimaChain) {
            bumpProgress('Sincronizando con OFIMA y facturación de remisión…')
            const r = await runOfimaChainBeforeFirstCierre(String(od.id), od.store_name || '')
            nrodcto = r.nrodcto
          } else {
            nrodcto = String(idRemisionStr).trim()
          }

          bumpProgress('Actualizando estado del pedido en el sistema…')
          const targetStatus = lista.every(p => Number(p.pago_verificado) === 1) ? 'abonado' : 'verify_transaction'
          const updatePlanSepare = prepararDatosActualizacion(targetStatus)
          await axiosInstance.patch(`/list/${od.id}`, updatePlanSepare, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          const latestOrderResponse = await axiosInstance.get(`/list/${od.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          const latestPagos = (
            latestOrderResponse.data?.extraDetails?.pagos ||
            latestOrderResponse.data?.pagos ||
            []
          ) as Array<{ id?: number }>

          let cierreIx = 0
          for (const i of nuevosIdx) {
            cierreIx += 1
            bumpProgress(`Aplicando recaudo (cierre) — abono ${cierreIx} de ${nuevosIdx.length}…`)

            const row = lista[i]
            const latestPago = Array.isArray(latestPagos) ? latestPagos[i] : null
            const idPedidoPago =
              (latestPago && typeof latestPago.id === 'number' ? latestPago.id : null) ||
              (typeof row.id === 'number' ? row.id : null)
            if (idPedidoPago === null) {
              toast.error(`No se encontró id_pedido_pago para el abono ${i + 1}`, {
                icon: '⚠️',
                position: 'top-center'
              })
              resetPlanSepareModalUi()
              return
            }
            await postRecaudoCierre({
              orderId: String(od.id),
              paymentMethod: 'plan_separe',
              nrodcto,
              tipodcto: 'RE',
              abono: {
                valor: Math.round(Number(row.valor) || 0),
                metodo_pago: (row.metodo_pago || '').toString().trim().toLowerCase(),
                id_transaccion: (row.id_transaccion || '').toString().trim() || null,
                id_pedido_pago: idPedidoPago
              }
            })
          }

          resetPlanSepareModalUi()

          toast.success(
            targetStatus === 'abonado' ? 'Plan Separe: pedido abonado' : 'Abonos verificados',
            { icon: '✅', position: 'top-center' }
          )
        } catch (planSepareErr: unknown) {
          console.error('[Plan Separe verificación]', planSepareErr)
          const apiBodyText = getPlanSepareApiErrorBodyText(planSepareErr)
          setPlanSepareModalUi({
            phase: 'error',
            step: 0,
            total: 0,
            label: '',
            errorMessage: apiBodyText
          })
          return
        }
      } else {
        // PRIMER PATCH: Cambiar estado a "processing" (incluye part / métodos simples)
        const updateDataProcessing = prepararDatosActualizacion('processing')
        await axiosInstance.patch(`/list/${orderDetail?.id}`, updateDataProcessing, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const transportadoraValue = orderDetail?.extraDetails?.transportadora || ''
        const isEntregaPDV = transportadoraValue === 'Entrega PDV'

        if (isEntregaPDV) {
          toast.success('Transacción verificada, en proceso de asignación...', { icon: '✅', position: 'top-center' })
          await new Promise(resolve => setTimeout(resolve, 2000))
          const updateDataAsignado = prepararDatosActualizacion('asignado')
          await axiosInstance.patch(`/list/${orderDetail?.id}`, updateDataAsignado, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          toast.success('Transacción verificada y pedido asignado exitosamente', { icon: '✅', position: 'top-center' })
        } else {
          const successMsg = isCash
            ? 'Pago en efectivo verificado correctamente'
            : 'Transacción verificada exitosamente'
          toast.success(successMsg, { icon: '✅', position: 'top-center' })
        }
      }

      try {
        await Promise.resolve(onRefreshOrderDetail?.())
      } catch (refreshErr) {
        console.error('Error al actualizar el detalle del pedido tras verificar:', refreshErr)
      }
      
      setShowVerifyPaymentModal(false)
      setShowCashModal(false)
      setTransactionId('')
      setMontoRecibido('')
      setPagosParcialesData({})
      setShowSuccessModal(true)
    } catch (error) {
      console.error("❌ Error al verificar el pago:", error)
      const isPlanSepareFlow = isPlanSepare && isPart
      const errorMessage = getVerifyPaymentErrorMessage(error, isPlanSepareFlow)
      if (axios.isAxiosError(error)) {
        console.error('📝 Detalles del error:', JSON.stringify({
          code: error.code,
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        }, null, 2))
      }
      if (isPlanSepareFlow && shouldRefreshOrderOnDatafTransactionRequired(error)) {
        try {
          await Promise.resolve(onRefreshOrderDetail?.())
        } catch (refreshErr) {
          console.error('Error recargando detalle del pedido tras transaction_id requerido:', refreshErr)
        }
      }
      toast.error(errorMessage, { icon: "⚠️", position: 'top-center' })
    } finally {
      setVerifyingPayment(false)
    }
  }
  const direcionBilling1 = orderDetail?.billingAddress?.address_1 || ''
  const direcionBilling2 = orderDetail?.billingAddress?.address_2 || ''
  const separador = direcionBilling2 ? ', ' : ''

  const direcionShipping1 = orderDetail?.shippingAddress?.address_1 || ''
  const direcionShipping2 = orderDetail?.shippingAddress?.address_2 || ''
  const separadorShipping = direcionShipping2 ? ', ' : ''

  // Obtener el nombre del agente
  const getAgenteNombre = () => {
    if (!orderDetail?.agente) return 'Sin agente asignado'
    
    if (typeof orderDetail.agente === 'object' && 'first_name' in orderDetail.agente && 'last_name' in orderDetail.agente) {
      const agente = orderDetail.agente as { first_name: string; last_name: string }
      return `${agente.first_name} ${agente.last_name}`.trim().replace(/\b\w/g, l => l.toUpperCase())
    }
    
    return 'Sin agente asignado'
  }

  return (
    <>
      {/* Botón flotante de notas del pedido (solo cuando hay pedido seleccionado) */}
      {orderDetail && (
        <NotesModal
          showModal={showNoteModal}
          setShowModal={setShowNoteModal}
          note={customerNote}
          onNoteChange={setCustomerNote}
          mode="edit"
          title="Notas del pedido"
          placeholder="Ingrese las notas del pedido..."
          position="right-4"
          buttonStyle="rounded-none"
        />
      )}

      <div className="space-y-8">
        {/* Nombre del Agente - Parte Superior Derecha */}

        {/* Información del Cliente */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="col-span-2">
          <h3 className="text-lg font-semibold text-blue-800 mb-4 col-span-1">Información del Cliente</h3>
          </div>
          <div className="text-sm text-gray-700 col-span-1 text-right">
            {getAgenteNombre()!=='Sin agente asignado' ? <><span className="font-semibold">Agente:</span> {getAgenteNombre()}</> : ''}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField label="Nombre" icon={getFieldIcon('name')}>
              <Input
                type="text" 
                value={`${orderDetail?.customer?.first_name || ''} ${orderDetail?.customer?.last_name || ''}`}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Email" icon={getFieldIcon('email')}>
              <Input
                type="email" 
                value={orderDetail?.customer?.email || ''} 
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Teléfono" icon={getFieldIcon('phone')}>
              <Input
                type="text" 
                value={orderDetail?.shippingAddress?.phone || orderDetail?.billingAddress?.phone || ''} 
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Tipo de Documento" icon={getFieldIcon('tipo_documento')}>
              <Input
                type="text" 
                value={getTipoDocumentoName(orderDetail?.customer?.tipo_documento || '')} 
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Documento" icon={getFieldIcon('documento')}>
              <Input
                type="text" 
                value={orderDetail?.customer?.document || ''} 
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Método de Pago" icon={getFieldIcon('payment_method')}>
              <Input
                type="text" 
                value={getPaymentMethodName(orderDetail?.extraDetails?.payment_method || '')} 
                readOnly
                mode="view"
              />
            </FormField>
          </div>
        </div>

        {/* Direcciones */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Dirección de Facturación */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Dirección de Facturación</h3>
            <div className="space-y-3">
              <FormField label="Dirección" icon={getFieldIcon('direccion')}>
                <Input
                  type="text" 
                  value={direcionBilling1 + separador + direcionBilling2 || ''}
                  readOnly
                  mode="view"
                />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Departamento" icon={getFieldIcon('departamento')}>
                  <Input
                    type="text" 
                    value={getDepartamentoNombre(orderDetail?.billingAddress?.state || '')} 
                    readOnly
                    mode="view"
                  />
                </FormField>
                <FormField label="Ciudad" icon={getFieldIcon('ciudad')}>
                  <Input
                    type="text" 
                    value={getCiudadNombre(orderDetail?.billingAddress?.city || '')} 
                    readOnly
                    mode="view"
                  />
                </FormField>
              </div>
            </div>
          </div>

          {/* Dirección de Envío */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Dirección de Envío</h3>
            <div className="space-y-3">
              <FormField label="Dirección" icon={getFieldIcon('direccion')}>
                <Input
                  type="text" 
                  value={direcionShipping1 + separadorShipping + direcionShipping2 || ''} 
                  readOnly
                  mode="view"
                />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Departamento" icon={getFieldIcon('departamento')}>
                  <Input
                    type="text" 
                    value={getDepartamentoNombre(orderDetail?.shippingAddress?.state || '')} 
                    readOnly
                    mode="view"
                  />
                </FormField>
                <FormField label="Ciudad" icon={getFieldIcon('ciudad')}>
                  <Input
                    type="text" 
                    value={getCiudadNombre(orderDetail?.shippingAddress?.city || '')} 
                    readOnly
                    mode="view"
                  />
                </FormField>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de ENVÍO */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">Envío</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <FormField label="Transportadora" icon={getFieldIcon('transportadora')}>
              <Input
                type="text"
                value={orderDetail?.extraDetails?.transportadora ? getTransportadoraName(orderDetail.extraDetails.transportadora) : 'No asignada'}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Guía" icon={getFieldIcon('guia')}>
              <Input
                type="text" 
                value={orderDetail?.guia || 'No asignada'} 
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Costo de envío" icon={getFieldIcon('envio')}>
              <Input
                type="text" 
                value={`$${parseFloat(orderDetail?.envio || '0').toLocaleString('es-CO')}`}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Estado" icon={getFieldIcon('status')}>
              <Input
                type="text" 
                value={getOrderStateName(orderDetail?.status || '')} 
                readOnly
                mode="view"
              />
            </FormField>
          </div>
          {customerNote.trim() && (
            <FormField label="Notas" icon={getFieldIcon('notas')} className="mt-4">
              <Textarea
                value={customerNote} 
                readOnly
                disabled={true}
                rows={2}
                mode="view"
              />
            </FormField>
          )}
        </div>

        {/* Productos del Pedido */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-800">Productos del Pedido</h3>
          </div>

          <OrderItemsTable
            lineItems={orderDetail?.lineItems || []}
            productDetails={productDetails}
            loadingDetails={loadingDetails}
            expandedCombos={expandedCombos}
            clienteNeto={isClienteNetoActive}
            mode="view"
            formDataStatus={orderDetail?.status}
            onQuantityChange={(index: number, quantity: number) => {console.log('Cambio de cantidad no permitido en modo view');}}
            onRemoveProduct={(index: number) => {console.log('Eliminación no permitida en modo view');}}
            onDiscountChange={(index: number, discount: number) => {console.log('Cambio de descuento no permitido en modo view');}}
            onPriceChange={(index: number, price: number) => {console.log('Cambio de precio no permitido en modo view');}}
            onToggleCombo={toggleCombo}
            calculateTotals={calculateTotals}
            shippingCost={orderDetail?.envio || '0'}
          />
        </div>

        {/* Botón verificar pago / abono (Plan Separe multipago) */}
        <div className="flex justify-end">
          <Button 
            variant="success"
            onClick={() => {
              const pmRaw = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
              if (normalizePaymentMethod(pmRaw) === 'cash') {
                setShowCashModal(true)
              } else if (isCmdmPaymentMethod(pmRaw)) {
                setShowCmdmCreditModal(true)
              } else {
                setShowVerifyPaymentModal(true)
              }
            }}
            leftIcon={CheckCircleIcon}
          >
            {isPlanSepareMultiOrder ? 'Verificar abono' : 'Verificar Pago'}
          </Button>
        </div>
      </div>

      {/* Modal de Verificación de Pago */}
      {showVerifyPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {isPlanSepareMultiOrder ? 'Verificar abono' : 'Verificar Pago'}
              </h2>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => { 
                  setShowVerifyPaymentModal(false)
                  setTransactionId('')
                  setMontoRecibido('')
                  setPagosParcialesData({})
                  resetPlanSepareModalUi()
                }}
                disabled={
                  isPlanSepareMultiOrder &&
                  planSepareModalUi.phase === 'progress' &&
                  verifyingPayment
                }
                className="!p-1"
              >
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>

            {isPlanSepareMultiOrder &&
              planSepareModalUi.phase === 'progress' &&
              verifyingPayment && (
              <div
                className="mb-4 rounded-lg border border-blue-100 bg-blue-50/90 p-4 space-y-3"
                role="status"
                aria-live="polite"
              >
                <p className="text-sm font-medium text-gray-800">{planSepareModalUi.label}</p>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="h-4 rounded-full transition-all duration-300 ease-out bg-blue-600"
                    style={{
                      width: `${
                        planSepareModalUi.total > 0
                          ? Math.min(
                              Math.round((planSepareModalUi.step / planSepareModalUi.total) * 100),
                              100
                            )
                          : 0
                      }%`
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>Paso {planSepareModalUi.step} de {planSepareModalUi.total}</span>
                  <span>
                    {planSepareModalUi.total > 0
                      ? `${Math.round(
                          Math.min(
                            (planSepareModalUi.step / planSepareModalUi.total) * 100,
                            100
                          )
                        )}%`
                      : '…'}
                  </span>
                </div>
                <div className="flex justify-center pt-1">
                  <LoadingSpinner size="medium" />
                </div>
              </div>
            )}

            {isPlanSepareMultiOrder && planSepareModalUi.phase === 'error' && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 space-y-4" role="alert">
                <p className="text-sm text-red-800">
                  {planSepareModalUi.errorMessage?.trim()
                    ? planSepareModalUi.errorMessage
                    : 'En este momento no pudimos continuar. Puedes probar de nuevo o cerrar con Aceptar para ver la información al día.'}
                </p>
              </div>
            )}

            <div className="space-y-6">
              {(() => {
                const paymentMethod = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
                const isCOD = paymentMethod === 'cod'
                const isPart = isMultiPaymentMethod(paymentMethod)
                const isPlanSepare = normalizePaymentMethod(paymentMethod) === 'plan_separe'
                
                if (isPart) {
                  // Mostrar todos los pagos parciales
                  const pagos = orderDetail?.extraDetails?.pagos || []
                  
                  if (pagos.length === 0) {
                    return (
                      <div className="text-center text-gray-500 py-4">
                        {isPlanSepare ? 'No hay abonos para verificar' : 'No hay pagos parciales para verificar'}
                      </div>
                    )
                  }
                  
                  return (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        {isPlanSepare ? 'Pago de abono' : 'Pagos Parciales'}
                      </h3>
                      {pagos.map((pago, index) => {
                        const pagoData = pagosParcialesData[index] || {}
                        const isAlreadyVerified = Number(pago.pago_verificado) === 1
                        const isPagoCOD = pago.metodo_pago === 'cod' || pago.metodo_pago === 'cash'
                        const isPagoOnline = isPlanSepare ? requiresTransactionIdInPlanSepare(pago.metodo_pago) : requiresTransactionIdInVerify(pago.metodo_pago)
                        
                        return (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="mb-3">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-semibold text-gray-700">
                                  {isPlanSepare ? `Abono ${index + 1}` : `Pago ${index + 1}`}
                                </span>
                                <div className="flex flex-col items-end gap-0.5 text-right">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600">
                                      {getPaymentMethodName(pago.metodo_pago)}
                                    </span>
                                    {isAlreadyVerified && (
                                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                                        Verificado
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-sm font-semibold text-orange-600">
                                    {formatPagoValorCop(pago.valor)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {isAlreadyVerified ? null : isPagoCOD && (
                              <FormField
                                label="Monto Recibido"
                                required
                                icon={getFieldIcon('monto')}
                              >
                                <Input
                                  type="number" 
                                  value={pagoData.monto || ''} 
                                  onChange={(e) => {
                                    // Limpiar puntos y comas del valor ingresado
                                    const valorLimpio = e.target.value.replace(/[.,]/g, '')
                                    setPagosParcialesData(prev => ({
                                      ...prev,
                                      [index]: { ...prev[index], monto: valorLimpio }
                                    }))
                                  }}
                                  placeholder="Ingrese el monto recibido"
                                  disabled={verifyingPayment}
                                  isLoading={verifyingPayment}
                                />
                              </FormField>
                            )}
                            
                            {!isAlreadyVerified && isPagoOnline && (
                              <FormField
                                label="ID Transacción"
                                required
                                icon={getFieldIcon('transaction_id')}
                              >
                                <Input
                                  type="text" 
                                  value={pagoData.transactionId || pago.id_transaccion || ''} 
                                  onChange={(e) => {
                                    setPagosParcialesData(prev => ({
                                      ...prev,
                                      [index]: { ...prev[index], transactionId: e.target.value }
                                    }))
                                  }}
                                  placeholder="Ingrese el ID de transacción"
                                  disabled={verifyingPayment}
                                  isLoading={verifyingPayment}
                                />
                                {pago.metodo_pago === 'bold' && (
                                  <BoldCheckoutLinkBelowId
                                    paymentId={pagoData.transactionId || pago.id_transaccion || ''}
                                  />
                                )}
                              </FormField>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                } else if (isCOD) {
                  return (
                    <FormField
                      label="Monto Recibido"
                      required
                      icon={getFieldIcon('monto')}
                    >
                      <Input
                        type="number" 
                        value={montoRecibido} 
                        onChange={(e) => {
                          // Limpiar puntos y comas del valor ingresado
                          const valorLimpio = e.target.value.replace(/[.,]/g, '')
                          setMontoRecibido(valorLimpio)
                        }}
                        placeholder="Ingrese el monto recibido"
                        disabled={verifyingPayment}
                        isLoading={verifyingPayment}
                      />
                    </FormField>
                  )
                } else {
                  const pmSingle = normalizePaymentMethod(
                    orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
                  )
                  const boldIdSingle =
                    (transactionId || orderDetail?.extraDetails?.transaction_id || '').trim()
                  return (
                    <FormField
                      label="ID Transacción"
                      required
                      icon={getFieldIcon('transaction_id')}
                    >
                      <Input
                        type="text" 
                        value={transactionId} 
                        onChange={(e) => setTransactionId(e.target.value)}
                        placeholder="Ingrese el ID de transacción"
                        disabled={verifyingPayment}
                        isLoading={verifyingPayment}
                      />
                      {pmSingle === 'bold' && <BoldCheckoutLinkBelowId paymentId={boldIdSingle} />}
                    </FormField>
                  )
                }
              })()}

              {/* Mensaje de campos faltantes para pagos parciales */}
              {(() => {
                const paymentMethod = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
                const isPart = isMultiPaymentMethod(paymentMethod)
                const isPlanSepare = normalizePaymentMethod(paymentMethod) === 'plan_separe'
                
                if (isPart && !verifyingPayment) {
                  const pagos = orderDetail?.extraDetails?.pagos || []
                  const camposFaltantes: string[] = []
                  
                  pagos.forEach((pago, index) => {
                    if (Number(pago.pago_verificado) === 1) {
                      return
                    }
                    const pagoData = pagosParcialesData[index] || {}
                    const fila = isPlanSepare ? `Abono ${index + 1}` : `Pago ${index + 1}`

                    if (pago.metodo_pago === 'cod' || pago.metodo_pago === 'cash') {
                      // Limpiar puntos y comas del monto
                      const montoLimpio = (pagoData.monto || '').replace(/[.,]/g, '')
                      if (!montoLimpio.trim() || isNaN(Number(montoLimpio)) || Number(montoLimpio) <= 0) {
                        camposFaltantes.push(`${fila}: Monto recibido`)
                      } else if (pago.metodo_pago === 'cod' || pago.metodo_pago === 'cash') {
                        // Validar que el monto coincida con el valor del pago
                        const valorPago = Math.round(Number(pago.valor) || 0)
                        const montoIngresado = Math.round(Number(montoLimpio))
                        if (montoIngresado !== valorPago) {
                          camposFaltantes.push(
                            `${fila}: El monto no coincide con el valor del ${isPlanSepare ? 'abono' : 'pago'}`
                          )
                        }
                      }
                    } else if (isPlanSepare ? requiresTransactionIdInPlanSepare(pago.metodo_pago) : requiresTransactionIdInVerify(pago.metodo_pago)) {
                      if (!pagoData.transactionId || !pagoData.transactionId.trim()) {
                        camposFaltantes.push(`${fila}: ID de transacción`)
                      }
                    }
                  })
                  
                  if (camposFaltantes.length > 0) {
                    return (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm font-semibold text-yellow-800 mb-1">
                          Faltan completar los siguientes campos:
                        </p>
                        <ul className="text-sm text-yellow-700 list-disc list-inside">
                          {camposFaltantes.map((campo, idx) => (
                            <li key={idx}>{campo}</li>
                          ))}
                        </ul>
                      </div>
                    )
                  }
                }
                return null
              })()}

              <div className="flex justify-end space-x-4 mt-6">
                <Button 
                  variant="secondary"
                  outline
                  onClick={() => { 
                    setShowVerifyPaymentModal(false)
                    setTransactionId('')
                    setMontoRecibido('')
                    setPagosParcialesData({})
                    resetPlanSepareModalUi()
                  }} 
                  disabled={
                    verifyingPayment ||
                    (isPlanSepareMultiOrder && planSepareModalUi.phase === 'progress')
                  }
                >
                  Cancelar
                </Button>
                <Button 
                  variant="success"
                  onClick={() => {
                    if (isPlanSepareMultiOrder && planSepareModalUi.phase === 'error') {
                      resetPlanSepareModalUi()
                    }
                    void handleVerifyPayment()
                  }}
                  disabled={(() => {
                    const paymentMethod = orderDetail?.extraDetails?.payment_method || orderDetail?.payment_method
                    const isCOD = paymentMethod === 'cod'
                    const isPart = isMultiPaymentMethod(paymentMethod)
                    const isPlanSepare = normalizePaymentMethod(paymentMethod) === 'plan_separe'
                    
                    if (isPart) {
                      const pagos = orderDetail?.extraDetails?.pagos || []
                      if (pagos.length === 0) return true
                      
                      // Validar que todos los pagos tengan sus datos
                      for (let i = 0; i < pagos.length; i++) {
                        const pago = pagos[i]
                        if (Number(pago.pago_verificado) === 1) {
                          continue
                        }
                        const pagoData = pagosParcialesData[i] || {}
                        
                        if (pago.metodo_pago === 'cod' || pago.metodo_pago === 'cash') {
                          // Limpiar puntos y comas del monto
                          const montoLimpio = (pagoData.monto || '').replace(/[.,]/g, '')
                          if (!montoLimpio.trim() || isNaN(Number(montoLimpio)) || Number(montoLimpio) <= 0) {
                            return true
                          }
                          // Validar que el monto coincida con el valor del pago
                          if (pago.metodo_pago === 'cod' || pago.metodo_pago === 'cash') {
                            const valorPago = Math.round(Number(pago.valor) || 0)
                            const montoIngresado = Math.round(Number(montoLimpio))
                            if (montoIngresado !== valorPago) {
                              return true
                            }
                          }
                        } else if (isPlanSepare ? requiresTransactionIdInPlanSepare(pago.metodo_pago) : requiresTransactionIdInVerify(pago.metodo_pago)) {
                          if (!pagoData.transactionId || !pagoData.transactionId.trim()) {
                            return true
                          }
                        }
                      }
                      return verifyingPayment
                    } else if (isCOD) {
                      const montoLimpio = montoRecibido.replace(/[.,]/g, '')
                      return !montoLimpio.trim() || isNaN(Number(montoLimpio)) || Number(montoLimpio) <= 0 || verifyingPayment
                    } else {
                      return !transactionId.trim() || verifyingPayment
                    }
                  })()}
                  isLoading={verifyingPayment}
                  leftIcon={CheckCircleIcon}
                >
                  {verifyingPayment
                    ? 'Verificando...'
                    : (isPlanSepareMultiOrder && planSepareModalUi.phase === 'error'
                      ? 'Reintentar'
                      : (isPlanSepareMultiOrder ? 'Verificar todos los abonos' : 'Verificar Todos los Pagos'))}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}


      <VerifyCmdmCreditModal
        isOpen={showCmdmCreditModal}
        order={orderDetail ?? undefined}
        pedidoTotalCop={cmdmPedidoTotalCop}
        onClose={() => {
          if (verifyingPayment) return
          setShowCmdmCreditModal(false)
        }}
        onApprove={handleCmdmApproveCredit}
        onReject={handleCmdmRejectCredit}
        isLoading={verifyingPayment}
      />
      {/* Modal de Cash - FormModal */}
      <FormModal
        isOpen={showCashModal}
        onClose={() => {
          setShowCashModal(false)
          setMontoRecibido('')
        }}
        title="Verificar Pago en Efectivo"
        inputLabel="Monto Recibido"
        inputValue={montoRecibido}
        onInputChange={(value) => {
          // Limpiar puntos y comas del valor ingresado
          const valorLimpio = value.replace(/[.,]/g, '')
          setMontoRecibido(valorLimpio)
        }}
        inputPlaceholder="Ingrese el monto recibido"
        inputRequired={true}
        confirmText="Verificar"
        cancelText="Cancelar"
        onConfirm={async () => {
          // Limpiar el valor antes de validar
          const montoLimpio = montoRecibido.replace(/[.,]/g, '')
          
          if (!montoLimpio.trim() || isNaN(Number(montoLimpio)) || Number(montoLimpio) <= 0) {
            toast.error("Por favor ingrese un monto válido", { icon: "⚠️", position: 'top-center' })
            return
          }
          
          // Validar que coincida con el total del pedido
          const totals = calculateTotals(orderDetail?.lineItems || [])
          const totalPedido = parseFloat(totals.total) + parseFloat(orderDetail?.envio || '0')
          const montoIngresado = Math.round(Number(montoLimpio))
          const totalRedondeado = Math.round(totalPedido)
          
          if (montoIngresado !== totalRedondeado) {
            toast.error(
              `El monto ingresado ($${montoIngresado.toLocaleString('es-CO')}) no coincide con el total del pedido ($${totalRedondeado.toLocaleString('es-CO')})`, 
              { icon: "⚠️", position: 'top-center' }
            )
            return
          }
          
          await handleVerifyPayment()
        }}
        isLoading={verifyingPayment}
        loadingText="Verificando..."
        disabled={(() => {
          const montoLimpio = montoRecibido.replace(/[.,]/g, '')
          if (!montoLimpio.trim() || isNaN(Number(montoLimpio)) || Number(montoLimpio) <= 0) {
            return true
          }
          // Validar que coincida con el total del pedido
          const totals = calculateTotals(orderDetail?.lineItems || [])
          const totalPedido = parseFloat(totals.total) + parseFloat(orderDetail?.envio || '0')
          const montoIngresado = Math.round(Number(montoLimpio))
          const totalRedondeado = Math.round(totalPedido)
          return montoIngresado !== totalRedondeado
        })()}
      />

      {/* Modal de Confirmación de Éxito */}
      <ConfirmationModal
        isOpen={showSuccessModal} onClose={() => { setShowSuccessModal(false); window.location.reload(); }}
        title="Pago Verificado" message="El pago ha sido verificado y el pedido procesado exitosamente."
        type="success" onConfirm={() => { setShowSuccessModal(false); window.location.reload(); }}
        confirmText="Aceptar"/>
    </>
  )
}

export default VerifyTransactionsForm
